import {
  BluePredicatenIcon,
  isMobile,
  ProductLogo,
  ProductOverviewModalTriplePlay,
  PromotionBar
} from '@kizi/comparator-components';
import { useField } from '@kizi/comparator-core';
import { Button, Div, P } from '@kizi/core-components';
import React from 'react';
import { DesktopCompareCheck } from './components/DesktopCompareCheck';
import { ProductCardMainSection } from './components/ProductCardMainSection';
import { InternetAndChannels } from './components/InternetAndChannels';
import { PriceButtonComp } from './components/PriceButtonComp';
import { NonPurchasableText } from './components/NonPurchasableText';
import { Checkbox, IconV2 } from '@kizi/shared-components';

const CompareComponent = ({ onMobileCompare, data }) => {
  return (
    <Checkbox
      mr={'8px'}
      variant="main"
      label="Vergelijk"
      id={`compare-id-${data?.id}`}
      labelPosition="right"
      containerProps={{
        fontFamily: "'Segoe UI', sans-serif",
        padding: '4px 16px',
      }}
      checked={data.compare}
      onChange={() => {
        onMobileCompare?.(data.id);
      }}
    />
  );
};

export const ProductCardTriplePlay = ({
  data,
  productTypes,
  onCompareChange,
  handleSubmit,
  productUspsContent,
  isOpen,
  toggleOpenState,
  onMobileCompare,
  ExpandedComponent,
  currentModalTab,
  setCurrentModalTab,
  openTab,
  promotionModals,
  hasPromotionBar,
}) => {
  const currentProviderField = useField('startWidget.currentProvider');

  const { purchasableProduct, provider, image, priceInfo, customAttributes } = data;

  const { discountIncluded, total } = priceInfo;

  const isCurrentTriplePlayProvider =
    provider?.externalId === currentProviderField?.value;

  const predicate = customAttributes?.find(
    a => a.description === 'Predicaat',
  );

  const isPurchasableProduct =
    purchasableProduct && !isCurrentTriplePlayProvider;
  const isNotPurchasableProduct =
    !purchasableProduct || isCurrentTriplePlayProvider;

  const PriceComponent = (
    <PriceButtonComp
      discountIncluded={discountIncluded}
      priceInitialValue={total}
      data={data}
      priceProps={data}
      handleSubmit={handleSubmit}
      isPurchasableProduct={isPurchasableProduct}
      isNotPurchasableProduct={isNotPurchasableProduct}
    />
  );

  return (
    <Div
      position="relative"
      mt="30px"
      css={{
        background: 'white',
        borderRadius: '10px',
        boxShadow: '0px 0px 20px 0px #00000026',
      }}
    >
      <Div
        css={{
          display: 'flex',
          flexDirection: 'column',
          outline: data.compare ? '2px solid #004DF5' : '',
          borderRadius: '10px',
          padding: ['20px', null, '30px 30px 40px 30px'],
        }}
      >
        {promotionModals && hasPromotionBar && (
          <PromotionBar
            incentive={data.incentive}
            productTypes={productTypes}
            items={promotionModals.items}
            css={{
              margin: [
                '-20px -20px 20px -20px',
                '-20px -20px 20px -20px',
                '-30px -30px 20px -30px',
              ],
              '> div': {
                paddingLeft: '0px',
              },
              svg: {
                marginRight: '4px',
              },
            }}
          />
        )}

        <Div
          css={{
            display: 'flex',
            gap: ['16px', '16px', '30px'],
            justifyContent: 'space-between',
            flexDirection: ['column', 'column', 'row'],
          }}
        >
          {/* 1st column */}
          <Div
            display="flex"
            justifyContent="space-between"
            alignItems={[null, 'unset']}
          >
            <Div
              display="flex"
              flexDirection="column"
              justifyContent={["center", null, "space-between"]}
              alignItems={[null, null, 'center']}
              gridGap={isMobile() ? 3 : null}
            >
              <Div
                maxWidth={'96px'}
                variant={isNotPurchasableProduct ? 'grayedContainer' : ''}
              >
                <ProductLogo image={image} width={'100%'}/>
              </Div>

              {!isMobile() && predicate?.value && (
                <BluePredicatenIcon/>
              )}

              {!isMobile() && (
                <Div variant={isNotPurchasableProduct ? 'grayedContainer' : ''}>
                  {DesktopCompareCheck(data, onCompareChange)}
                </Div>
              )}
            </Div>

            <Div display={['flex', null, 'none']}>
              {predicate?.value && (
                <BluePredicatenIcon/>
              )}
            </Div>

            <Div display={['flex', null, 'none']}>{PriceComponent}</Div>
          </Div>

          {/* 2nd column */}
          <ProductCardMainSection
            data={data}
            productUspsContent={productUspsContent}
            setCurrentModalTab={setCurrentModalTab}
          />

          {/* 3rd column */}
          <InternetAndChannels
            data={data}
            productTypes={productTypes}
            openTab={openTab}
          />

          {/* 4th column */}
          <Div>
            <Div display={['block', null, 'none']}>
              {isPurchasableProduct && (
                <Button
                  onClick={handleSubmit}
                  variant="productCardPrimary"
                  padding={'8px 24px'}
                >
                  Bekijk
                </Button>
              )}

              {isNotPurchasableProduct && <NonPurchasableText />}

              <Div
                display={'flex'}
                alignItems={'center'}
                justifyContent={'space-between'}
                mt={'8px'}
                height={'40px'}
              >
                <P
                  variant="bodyRegular"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOpenState();
                  }}
                  color={'main.1'}
                  css={{
                    boxSizing: 'border-box',
                    textAlign: 'center',
                    minWidth: '116px',
                    padding: '4px 16px',
                    fontSize: '14px !important',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {isOpen ? 'Minder informatie' : 'Meer informatie'}

                  <IconV2
                    size={20}
                    name={'big-arrow-up'}
                    fill="#004DF5"
                    css={{
                      transform: isOpen ? 'unset' : 'rotate(180deg)',
                    }}
                  />
                </P>

                <CompareComponent
                  onMobileCompare={() => onMobileCompare(data.id)}
                  data={data}
                />
              </Div>
            </Div>
            <Div display={['none', null, 'block']} height={'100%'}>
              {PriceComponent}
            </Div>
          </Div>
        </Div>

        {isOpen && (
          <Div
            css={{
              marginTop: '24px',
              // Workaround until expanded Component is specific for each component
              // Targeting elements having styles from the monster product variant
              '> div > div > div > div > div >': {
                border: '1px solid red',

                'div:first-child': {
                  width: '100%',
                  marginRight: '12px',
                },

                'div:nth-child(2)': {
                  width: '100%',
                },
              },

              '> div:first-child': {
                padding: 0,
              },
            }}
          >
            {ExpandedComponent}
          </Div>
        )}
      </Div>

      <Button
        display={['none', null, 'flex']}
        onClick={toggleOpenState}
        variant={'expandProductCard'}
      >
        {isOpen ? 'Minder informatie' : 'Meer informatie'}
      </Button>

      <ProductOverviewModalTriplePlay
        startIndex={currentModalTab}
        onClose={(e) => {
          e.stopPropagation();
          setCurrentModalTab(-1);
        }}
        priceMode={'month'}
        product={data}
        onSubmit={handleSubmit}
        services={data.servicesWrapper}
      />
    </Div>
  );
};
